import { useState,useEffect, FunctionComponent } from 'react';
import AuthLayoutSubmitButton from '../../components/shared-components/buttons/AuthLayoutSubmitButton'
import styles from './ResetPassword.module.css'
import { useForm } from 'react-hook-form';
import { CircularProgress, Dialog, IconButton, InputAdornment} from '@mui/material';
import PopupComponent from '../../components/shared-components/success-popup/PopupComponent';
import { useNavigate } from 'react-router-dom'
import images from '../../assets/images';
import { CustomPaperComponent, CustomTextField, maxLengthValidation, minLengthValidation, passwordRegexPattern } from '../../helper';
import { useLazyQuery, useMutation } from '@apollo/client';
import { RESET_PASSWORD, VERIFY_TOKEN } from './api/ResetPassword';
import ToasterWithMessage from '../../components/shared-components/toaster';

interface ResetSubmitData {
    newpassword: string,
    retypepassword: string
}

const NewPasswordTextField = CustomTextField();
const RetypePasswordTextField = CustomTextField();

const ResetPassword : FunctionComponent= () => {
    const navigate = useNavigate();
    const {error} = ToasterWithMessage()
    const { passwordHideIcon, passwordUnHideIcon } = images;
    const { register, handleSubmit, reset, formState: { errors }} = useForm({ defaultValues: { newpassword: "", retypepassword: "" }, mode: "onTouched" });
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [jwtToken, setJwtToken] = useState<string | null>("")
    const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
    const [showRetypePassword, setShowRetypePassword] = useState<boolean>(false);
    const [mismatchError, setMismatchError] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [disable,setDisable] = useState<boolean>(false);
    //overrides the mui paper 
    const StyledComponent = CustomPaperComponent();

    const [verifyToken] = useLazyQuery(VERIFY_TOKEN, {
      fetchPolicy: "no-cache",
      onCompleted: (_data) => {
      },
      onError: (errorMsg) => {
        error(errorMsg.message);
        navigate('/login')
      },
    });

    useEffect(() => {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const token = urlParams.get('token');
        setJwtToken(token)
        verifyToken({ variables: {token}});
      },[verifyToken])

   const [resetPassword] = useMutation(RESET_PASSWORD, {
     fetchPolicy: "no-cache",
     onCompleted: (resetData) => {
       setLoading(false);
       const resetPasswordResponse = resetData?.resetPassword?.message;
       if (resetPasswordResponse === "Password updated successfully.") {
         setOpenDialog(true);
       }
     },
     onError: (err) => {
       setLoading(false);
       setDisable(!disable);
       error(err.message);
       setTimeout(() => {
         navigate("/login");
       }, 3000);
     },
   });
    
    //for new password
    const handleClickShowNewPassword = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setShowNewPassword((show) => !show);
    }
    //for retype password
    const handleClickShowRetypePassword = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setShowRetypePassword((show) => !show);
    }
    //closes the success pop
    const handleCloseDialog = () => {
        setOpenDialog(false);
        navigate('/login')
    };
   
    const onSubmitData = (data: ResetSubmitData) => {     
      if (data.newpassword !== data.retypepassword) {
        setDisable(disable);
        setMismatchError("New password and retype password is mismatched");
      } else {
        setDisable(!disable);
        setLoading(true)
        resetPassword({
          variables: {
            resetPasswordInput: {
              newPassword: data.newpassword,
              token: jwtToken,
            },
          },
        });
        setMismatchError("");
        reset();
      }
    };
    return (
        <>
            <form onSubmit={handleSubmit(onSubmitData)}>
                <div className={styles.resetForm}>
                    <div className={styles.textField}>
                        <div className={styles.textFieldFullMain}>
                            <div className={styles.titlePlaceholder}>
                                <div className={styles.newPassword}>New Password</div>
                            </div>
                                <NewPasswordTextField
                                    variant={'outlined'}
                                    fullWidth
                                    type={showNewPassword ? 'text' : 'password'}
                                    {...register("newpassword",
                                    {
                                        required:"New password is required!",
                                        minLength: minLengthValidation,
                                        maxLength: maxLengthValidation,
                                        pattern: {
                                            value: passwordRegexPattern,
                                            message: 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
                                        }
                                    })}
                                    placeholder='Enter new password'
                                    InputProps={{
                                        style: {
                                            borderRadius: 0,
                                            fontFamily: 'var(--body-b3-12-semibold-left)',
                                            fontSize: 'var(--body-b2-14-semibold-left-size)',
                                        },
                                        endAdornment: <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowNewPassword}
                                                edge="end"
                                                disableRipple
                                            >
                                                {showNewPassword ? <img className="passwordUnHideIconStyle" src={passwordUnHideIcon} alt='UnHideIcon' /> : <img src={passwordHideIcon} alt='HideIcon'/>}
                                            </IconButton>
                                        </InputAdornment>
                                    }}
                                    error={Boolean(errors.newpassword?.message)}
                                />
                            <span className={styles.errorMsgStyle}>{errors?.newpassword?.message}</span>
                        </div>
                        <div className={styles.textFieldFullMain}>
                            <div className={styles.titlePlaceholder}>
                                <div className={styles.newPassword}>Retype Password</div>
                            </div>
                            <RetypePasswordTextField
                                variant='outlined'
                                type={showRetypePassword ? "text" : "password"}
                                {...register("retypepassword", {
                                    required: "Retype Password is required!",
                                    minLength: minLengthValidation,
                                    maxLength: maxLengthValidation,
                                    pattern: {
                                        value: passwordRegexPattern,
                                        message:   'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
                                    }
                                })}
                                fullWidth
                                placeholder={"Enter retype password"}
                                InputProps={{
                                    style: {
                                        borderRadius: 0,
                                        fontFamily: 'var(--body-b3-12-semibold-left)',
                                        fontSize: 'var(--body-b2-14-semibold-left-size)',
                                    },
                                    endAdornment: <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowRetypePassword}
                                            edge="end"
                                            disableRipple
                                        >
                                            {showRetypePassword ? <img className="passwordUnHideIconStyle" src={passwordUnHideIcon} alt='UnHideIcon' /> : <img src={passwordHideIcon} alt='HideIcon'/> }
                                        </IconButton>
                                    </InputAdornment>
                                }}
                                error={Boolean(errors?.retypepassword?.message) || Boolean(mismatchError)}
                            />
                            <span className={styles.errorMsgStyle}>{errors?.retypepassword?.message || mismatchError}</span>
                        </div>
                    </div>
                    {
                        loading
                    ?
                        <div className={styles.resetCircular}>
                        <CircularProgress color={"primary"} size={30}/>
                        </div>
                    : ''
                    }
                    <AuthLayoutSubmitButton title={"Change Password"} disableBtn={disable} />
                </div>
                <Dialog
                    open={openDialog}
                    onClose={(_event, reason) => {
                        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                        handleCloseDialog();
                    }}
                    PaperComponent={StyledComponent}
                >
                    <PopupComponent handleClose={handleCloseDialog} title={"Password Updated"} subTitle={"Your password has been successfully updated. Please sign in to your account."}/>
                </Dialog>
            </form>
        </>
    )
}

export default ResetPassword
